// Map registry (render side). Every src/maps/<id>/render.ts exporting `RENDER: MapRenderDef` is picked up automatically;
// a map without one falls back to a plain placeholder look so it still runs. Render-only (imports three).
//
// LOAD-PERF (owner 2026-11-02: "load quickly … especially loading the maps"): each map's render module is its own chunk.
// Boot loads the title map before building the view (loadMapRender) and prefetches the others right after (they are small
// and evaluate instantly); Start Run / the map pre-build await loadMapRender() for their map, so mapRender() below only
// ever sees loaded maps.
import type { MapRenderDef } from '../render/mapkit';
import { basicEnv, BASE_LOOK } from '../render/mapkit';
import { mapById } from './registry';

const found = import.meta.glob<{ RENDER: MapRenderDef }>('./*/render.ts');
const BY_ID = new Map<string, MapRenderDef>();
const LOADING = new Map<string, Promise<MapRenderDef | null>>();
const HAS = new Set(Object.keys(found).map((p) => p.split('/')[1]));

/** Load (once) the render module of a map; resolves to its def (null = the map has no render.ts: placeholder look). */
export function loadMapRender(id: string): Promise<MapRenderDef | null> {
  const folder = mapById(id).id;
  if (BY_ID.has(folder)) return Promise.resolve(BY_ID.get(folder)!);
  if (!HAS.has(folder)) return Promise.resolve(null);
  let p = LOADING.get(folder);
  if (!p) {
    p = found[`./${folder}/render.ts`]().then((m) => {
      if (!m.RENDER || m.RENDER.id !== folder) throw new Error(`map folder ${folder}: render.ts must export RENDER with id '${folder}'`);
      BY_ID.set(folder, m.RENDER); return m.RENDER;
    });
    LOADING.set(folder, p); p.catch(() => LOADING.delete(folder));
  }
  return p;
}
/** true once mapRender(id) returns the map's real def */
export function mapRenderLoaded(id: string): boolean { const f = mapById(id).id; return BY_ID.has(f) || !HAS.has(f); }
/** Fetch + evaluate every map's render module (boot calls this once the title map is up). */
export function prefetchMapRenders(): Promise<unknown> { return Promise.allSettled([...HAS].map((id) => loadMapRender(id))); }

/** Render def for a map id (unknown / missing → the core registry's default map, or a neutral placeholder). */
export function mapRender(id: string): MapRenderDef {
  const def = mapById(id);
  const r = BY_ID.get(def.id);
  if (!r && HAS.has(def.id)) console.warn(`[maps] render module of ${def.id} not loaded yet (call loadMapRender first)`);
  return r ?? { id: def.id, look: BASE_LOOK, buildEnv: (ctx) => basicEnv(ctx, {}) };
}
